import type { ComponentType, ReactNode } from 'react';
import {
  Ban,
  BadgeCheck,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CirclePause,
  CircleX,
  Clock,
  Eye,
  Hourglass,
  Inbox,
  LoaderCircle,
  Lock,
  MailOpen,
  PackageCheck,
  RotateCcw,
  Send,
  TriangleAlert,
  type LucideProps,
} from 'lucide-react';
import {
  ACCESS_STATE_LABELS,
  ASSET_STATE_LABELS,
  ENGAGEMENT_STATE_LABELS,
  FORM_STATE_LABELS,
  type AccessState,
  type AssetState,
  type EngagementState,
  type FormState,
  type InvitationState,
  type TaskState,
} from '@cop/shared';
import { cx } from '@/lib/cx';
import { humanize } from '@/lib/format';

export type Tone = 'neutral' | 'info' | 'progress' | 'success' | 'warning' | 'danger' | 'muted';
type Icon = ComponentType<LucideProps>;

export interface StatusDescriptor {
  tone: Tone;
  icon: Icon;
  label: string;
}

const ENGAGEMENT: Record<EngagementState, Omit<StatusDescriptor, 'label'>> = {
  draft: { tone: 'neutral', icon: CircleDashed },
  invited: { tone: 'info', icon: Send },
  in_progress: { tone: 'progress', icon: CircleDot },
  submitted: { tone: 'info', icon: Inbox },
  in_review: { tone: 'progress', icon: Eye },
  changes_requested: { tone: 'warning', icon: RotateCcw },
  awaiting_client_approval: { tone: 'warning', icon: Hourglass },
  ready_for_handoff: { tone: 'success', icon: PackageCheck },
  completed: { tone: 'success', icon: CircleCheck },
  on_hold: { tone: 'muted', icon: CirclePause },
  cancelled: { tone: 'muted', icon: CircleX },
};

const FORM: Record<FormState, Omit<StatusDescriptor, 'label'>> = {
  draft: { tone: 'neutral', icon: CircleDashed },
  in_progress: { tone: 'progress', icon: CircleDot },
  submitted: { tone: 'info', icon: Inbox },
  in_review: { tone: 'progress', icon: Eye },
  changes_requested: { tone: 'warning', icon: RotateCcw },
  accepted: { tone: 'success', icon: CircleCheck },
};

const ASSET: Record<AssetState, Omit<StatusDescriptor, 'label'>> = {
  requested: { tone: 'neutral', icon: CircleDashed },
  provided: { tone: 'info', icon: Inbox },
  in_review: { tone: 'progress', icon: Eye },
  accepted: { tone: 'success', icon: CircleCheck },
  changes_requested: { tone: 'warning', icon: RotateCcw },
  na: { tone: 'muted', icon: Ban },
  waived: { tone: 'muted', icon: BadgeCheck },
  expired: { tone: 'danger', icon: Clock },
};

const ACCESS: Record<AccessState, Omit<StatusDescriptor, 'label'>> = {
  not_requested: { tone: 'neutral', icon: CircleDashed },
  requested: { tone: 'info', icon: Send },
  client_granted: { tone: 'progress', icon: Hourglass },
  verified: { tone: 'success', icon: CircleCheck },
  rejected: { tone: 'danger', icon: CircleX },
  revoked: { tone: 'danger', icon: Ban },
  expired: { tone: 'danger', icon: Clock },
  not_required: { tone: 'muted', icon: Ban },
  waived: { tone: 'muted', icon: BadgeCheck },
};

const TASK: Record<TaskState, StatusDescriptor> = {
  open: { tone: 'neutral', icon: CircleDashed, label: 'Open' },
  in_progress: { tone: 'progress', icon: LoaderCircle, label: 'In progress' },
  blocked: { tone: 'danger', icon: TriangleAlert, label: 'Blocked' },
  done: { tone: 'success', icon: CircleCheck, label: 'Done' },
  cancelled: { tone: 'muted', icon: CircleX, label: 'Cancelled' },
};

const INVITATION: Record<InvitationState, StatusDescriptor> = {
  created: { tone: 'neutral', icon: CircleDashed, label: 'Created' },
  sent: { tone: 'info', icon: Send, label: 'Sent' },
  opened: { tone: 'progress', icon: MailOpen, label: 'Opened' },
  accepted: { tone: 'success', icon: CircleCheck, label: 'Accepted' },
  expired: { tone: 'muted', icon: Clock, label: 'Expired' },
  revoked: { tone: 'muted', icon: Ban, label: 'Revoked' },
};

const APPROVAL: Record<string, StatusDescriptor> = {
  not_required: { tone: 'muted', icon: Ban, label: 'Not required' },
  not_requested: { tone: 'neutral', icon: CircleDashed, label: 'Not requested' },
  pending: { tone: 'warning', icon: Hourglass, label: 'Awaiting approval' },
  approved: { tone: 'success', icon: CircleCheck, label: 'Approved' },
  changes_requested: { tone: 'warning', icon: RotateCcw, label: 'Changes requested' },
  stale: { tone: 'danger', icon: TriangleAlert, label: 'Out of date' },
  waived: { tone: 'muted', icon: BadgeCheck, label: 'Waived' },
  superseded: { tone: 'muted', icon: Clock, label: 'Superseded' },
  cancelled: { tone: 'muted', icon: CircleX, label: 'Cancelled' },
};

const PARTICIPANT: Record<string, StatusDescriptor> = {
  invited: { tone: 'info', icon: Send, label: 'Invited' },
  active: { tone: 'success', icon: CircleCheck, label: 'Active' },
  suspended: { tone: 'warning', icon: CirclePause, label: 'Suspended' },
  removed: { tone: 'muted', icon: Ban, label: 'Removed' },
};

const CLIENT: Record<string, StatusDescriptor> = {
  active: { tone: 'success', icon: CircleCheck, label: 'Active' },
  archived: { tone: 'muted', icon: Lock, label: 'Archived' },
};

const PROFILE_REVISION: Record<string, StatusDescriptor> = {
  proposed: { tone: 'warning', icon: Hourglass, label: 'Pending decision' },
  applied: { tone: 'success', icon: CircleCheck, label: 'Applied' },
  rejected: { tone: 'muted', icon: CircleX, label: 'Rejected' },
};

export type StatusKind = 'engagement' | 'form' | 'asset' | 'access' | 'task' | 'invitation' | 'approval' | 'participant' | 'client' | 'profileRevision';

const FALLBACK: StatusDescriptor = { tone: 'neutral', icon: CircleDashed, label: '' };

/** Maps a shared state enum value to tone + icon + label. Unknown values degrade to neutral with a humanised label. */
export function describeStatus(kind: StatusKind, value: string): StatusDescriptor {
  const pick = <T extends Record<string, Omit<StatusDescriptor, 'label'>>>(map: T, labels: Record<string, string>) => {
    const d = map[value as keyof T];
    return d ? { ...d, label: labels[value] ?? humanize(value) } : { ...FALLBACK, label: humanize(value) };
  };
  const full = (map: Record<string, StatusDescriptor>) => map[value] ?? { ...FALLBACK, label: humanize(value) };
  switch (kind) {
    case 'engagement':
      return pick(ENGAGEMENT, ENGAGEMENT_STATE_LABELS);
    case 'form':
      return pick(FORM, FORM_STATE_LABELS);
    case 'asset':
      return pick(ASSET, ASSET_STATE_LABELS);
    case 'access':
      return pick(ACCESS, ACCESS_STATE_LABELS);
    case 'task':
      return full(TASK);
    case 'invitation':
      return full(INVITATION);
    case 'approval':
      return full(APPROVAL);
    case 'participant':
      return full(PARTICIPANT);
    case 'client':
      return full(CLIENT);
    case 'profileRevision':
      return full(PROFILE_REVISION);
  }
}

export interface StatusPillProps {
  kind: StatusKind;
  value: string;
  size?: 'sm' | 'md';
  className?: string;
  /** Override the label (tone/icon still come from the value). */
  label?: string;
}

/** Status indicator that never relies on colour alone: icon + text label, tone for scanning. */
export function StatusPill({ kind, value, size = 'md', className, label }: StatusPillProps) {
  const d = describeStatus(kind, value);
  const Icon = d.icon;
  return (
    <span className={cx('pill', `tone-${d.tone}`, `pill-${size}`, className)} data-status={value}>
      <Icon size={size === 'sm' ? 12 : 13} strokeWidth={2.25} aria-hidden />
      <span>{label ?? d.label}</span>
    </span>
  );
}

export interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
  variant?: 'soft' | 'outline';
}

/** Generic label/count badge. For workflow status use StatusPill instead. */
export function Badge({ tone = 'neutral', children, icon, className, variant = 'soft' }: BadgeProps) {
  return (
    <span className={cx('badge', `tone-${tone}`, variant === 'outline' && 'badge-outline', className)}>
      {icon && <span aria-hidden className="badge-icon">{icon}</span>}
      {children}
    </span>
  );
}
